import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Search, User, Eye, Trash, Edit, Plus } from 'lucide-react';
import { toast } from 'react-toastify';
import debounce from 'lodash/debounce';
import { Oval } from 'react-loader-spinner';

import { 
    fetchAlumni, 
    deleteAlumnus, 
    importAlumni,
    createAlumnus, 
    updateAlumnus
} from '../redux/AlumniSlice';
import { formatDate } from '../utils/Config';
import { usePagination } from '../hooks/usePagination';
import { Pagination } from '../utils/Pagination';

// Add Alumni Modal Component
const AddAlumniModal = ({ onClose, onAdd }) => {
    const [newAlumni, setNewAlumni] = useState({
        name: '',
        email: '',
        phone: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewAlumni(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Basic validation
        if (!newAlumni.name || !newAlumni.email || !newAlumni.phone) {
            toast.error('Please fill in all fields');
            return;
        }

        try {
            setIsSubmitting(true);
            await onAdd(newAlumni);
            onClose();
        } catch (error) {
            // Error handling is done in the parent component
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full mx-6 lg:max-w-md">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold">Add New Alumni</h2>
                    <button 
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700"
                    >
                        ✕
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <input
                        type="text"
                        name="name"
                        value={newAlumni.name}
                        onChange={handleInputChange}
                        placeholder="Full Name"
                        className="w-full px-3 py-2 border rounded-md"
                        required
                    />
                    <input
                        type="email"
                        name="email"
                        value={newAlumni.email}
                        onChange={handleInputChange}
                        placeholder="Email Address"
                        className="w-full px-3 py-2 border rounded-md"
                        required
                    />
                    <input
                        type="tel"
                        name="phone"
                        value={newAlumni.phone}
                        onChange={handleInputChange}
                        placeholder="Phone Number"
                        className="w-full px-3 py-2 border rounded-md"
                        required
                    />
                    <div className="flex justify-end gap-2 mt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <div className="flex justify-center items-center">
                                    <Oval
                                        visible={true}
                                        height="20"
                                        width="20"
                                        color="#ffff"
                                        ariaLabel="oval-loading"
                                        wrapperStyle={{}}
                                        wrapperClass=""
                                    />
                                </div>
                            ) : 'Add Alumni'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const AlumniModal = ({ alumni, onClose, onEdit }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editedAlumni, setEditedAlumni] = useState({
        name: alumni.name,
        email: alumni.email,
        phone: alumni.phone
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEditedAlumni(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSave = () => {
        // Implement save logic here
        onEdit(alumni.id, editedAlumni);
        setIsEditing(false);
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full mx-6 lg:max-w-md">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold">Alumni Details</h2>
                    <button 
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700"
                    >
                        ✕
                    </button>
                </div>

                {!isEditing ? (
                    <div>
                        <p><strong>Name:</strong> {alumni.name}</p>
                        <p><strong>Email:</strong> {alumni.email}</p>
                        <p><strong>Phone:</strong> {alumni.phone}</p>
                        <p><strong>Created At:</strong> {formatDate(alumni.created_at)}</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <input
                            type="text"
                            name="name"
                            value={editedAlumni.name}
                            onChange={handleInputChange}
                            placeholder="Name"
                            className="w-full px-3 py-2 border rounded-md"
                        />
                        <input
                            type="email"
                            name="email"
                            value={editedAlumni.email}
                            onChange={handleInputChange}
                            placeholder="Email"
                            className="w-full px-3 py-2 border rounded-md"
                        />
                        <input
                            type="tel"
                            name="phone"
                            value={editedAlumni.phone}
                            onChange={handleInputChange}
                            placeholder="Phone"
                            className="w-full px-3 py-2 border rounded-md"
                        />
                    </div>
                )}

                <div className="flex justify-end gap-2 mt-4">
                    {!isEditing ? (
                        <button
                            onClick={() => setIsEditing(true)}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                        >
                            Edit
                        </button>
                    ) : (
                        <>
                            <button
                                onClick={() => setIsEditing(false)}
                                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                            >
                                Save
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

const AlumniSearch = () => {
    const dispatch = useDispatch();
    const [searchTerm, setSearchTerm] = useState('');
    const { loading } = useSelector((state) => state.alumni);

    const debouncedSearch = useCallback(
        debounce((term) => {
            dispatch(fetchAlumni({
                page: 1,
                limit: 15,
                search: term
            }));
        }, 500),
        [dispatch]
    );

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        debouncedSearch(value);
    };

    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    return (
        <div className="relative">
            <div className="relative">
                <Search
                    className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${loading ? 'text-blue-500 animate-pulse' : 'text-gray-400'
                        }`}
                    size={20}
                />
                <input
                    type="text"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    placeholder="Search alumni..."
                    className="pl-10 pr-4 py-2 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                />
            </div>
        </div>
    );
};

const AlumniTable = ({ alumni, onView, onDelete }) => {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [alumniToDelete, setAlumniToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const confirmDelete = (alumni) => {
        setAlumniToDelete(alumni);
        setShowDeleteModal(true);
    };

    const handleDelete = async (id) => {
        try {
            setIsDeleting(true);
            await onDelete(id);
            setShowDeleteModal(false);
            setAlumniToDelete(null);
        } catch (error) {
            // Error handling is done in the parent component
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <div className="overflow-x-auto">
            <table className="min-w-full bg-white rounded-lg overflow-hidden">
                <thead className="bg-gray-50">
                    <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Alumni
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Email
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Phone
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                        </th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                    {alumni?.map((alumnus) => (
                        <tr key={alumnus?.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0 h-10 w-10">
                                        <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                            <User className="h-6 w-6 text-gray-400" />
                                        </div>
                                    </div>
                                    <div className="ml-4">
                                        <div className="text-sm font-medium text-gray-900">
                                            {alumnus?.name}
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td className="px-6 py-4">
                                <div className="text-sm text-gray-900">{alumnus?.email}</div>
                            </td>
                            <td className="px-6 py-4">
                                <div className="text-sm text-gray-900">{alumnus?.phone}</div>
                            </td>
                            <td className="px-6 py-4">
                                <div className="flex gap-2 items-center">
                                    <button
                                        onClick={() => onView(alumnus)}
                                        className="text-blue-600 hover:text-blue-900 hover:underline flex gap-1 items-center justify-center"
                                        title="View Details"
                                    >
                                        <Eye className="h-5 w-5" />
                                    </button>
                                    <button
                                        onClick={() => confirmDelete(alumnus)}
                                        className="text-red-600 hover:text-red-700"
                                        title="Delete Alumni"
                                    >
                                        <Trash className="h-5 w-5" />
                                    </button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {showDeleteModal && alumniToDelete && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full mx-6 lg:max-w-md">
                        <h2 className="text-xl font-bold mb-4">
                            Delete Alumni
                        </h2>
                        <p>Are you sure you want to delete {alumniToDelete.name}?</p>
                        <div className="flex justify-end gap-2 mt-4">
                            <button
                                onClick={() => {
                                    setShowDeleteModal(false);
                                    setAlumniToDelete(null);
                                }}
                                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type='button'
                                onClick={() => handleDelete(alumniToDelete.id)}
                                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                            >
                                {isDeleting ? (
                                    <div className="flex justify-center items-center">
                                        <Oval
                                            visible={true}
                                            height="20"
                                            width="20"
                                            color="#ffff"
                                            ariaLabel="oval-loading"
                                            wrapperStyle={{}}
                                            wrapperClass=""
                                        />
                                    </div>
                                ) : 'Confirm'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};



const Alumni = () => {
    const dispatch = useDispatch();
    const { items: alumni, loading, meta, error } = useSelector((state) => state.alumni);
    const [selectedAlumni, setSelectedAlumni] = useState(null);
    const [isImporting, setIsImporting] = useState(false);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);

    const {
        pagination,
        handlePageChange,
        updatePaginationData,
        getPageNumbers,
        getItemRange
    } = usePagination({
        initialPage: meta?.current_page || 1,
        initialPerPage: meta?.per_page || 15
    });

    useEffect(() => {
        handleFetchAlumni();
    }, [pagination.currentPage]);

    const handleFetchAlumni = () => {
        dispatch(fetchAlumni({
            page: pagination.currentPage,
            limit: pagination.perPage
        }))
        .unwrap()
        .then(response => {
            if (response.meta) {
                updatePaginationData({
                    current_page: response.meta.current_page,
                    last_page: response.meta.last_page,
                    per_page: response.meta.per_page,
                    total: response.meta.total
                });
            }
        })
        .catch((error) => {
            toast.error(error.message || 'Failed to fetch alumni');
        });
    };

    const handleImportChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setIsImporting(true);
            dispatch(importAlumni(file))
                .unwrap()
                .then(() => {
                    toast.success('Alumni imported successfully');
                    handleFetchAlumni();
                })
                .catch((error) => {
                    toast.error(error?.message || 'Failed to import alumni');
                })
                .finally(() => {
                    setIsImporting(false);
                });
        }
    };

    const handleDeleteAlumni = async (id) => {
        try {
            await dispatch(deleteAlumnus(id)).unwrap();
            toast.success('Alumni deleted successfully');
            // handleFetchAlumni();
        } catch (error) {
            const errorMessage = error?.error || error?.message || 'Failed to delete alumni';
            toast.error(errorMessage);
        }
    };

    const handleAddAlumni = async (alumniData) => {
        try {
            await dispatch(createAlumnus(alumniData)).unwrap();
            toast.success('Alumni added successfully');
            handleFetchAlumni();
        } catch (error) {
            const errorMessage = error?.error || error?.message || 'Failed to add alumni';
            toast.error(errorMessage);
        }
    };

    const handleEditAlumni = async (id, updatedData) => {
        try {
            const responseData = await dispatch(updateAlumnus({ id, data: updatedData })).unwrap();
            toast.success('Alumni updated successfully');
            setSelectedAlumni(responseData);
            // handleFetchAlumni();
        } catch (error) {
            const errorMessage = error?.error || error?.message || 'Failed to update alumni';
            toast.error(errorMessage);
        }
    };

    return (
        <div className="p-4">
            <div className="flex flex-col gap-2 sm:flex-row justify-between items-center mb-6">
                <h1 className="text-2xl font-semibold">Alumni</h1>
                <AlumniSearch />
            </div>

            <div className='flex justify-between items-center mb-4'>
                <button
                    onClick={() => setIsAddModalOpen(true)}
                    className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                >
                    <Plus className="h-5 w-5" />
                    Add Alumni
                </button>

                <div className='relative'>
                    <input 
                        type="file" 
                        accept=".csv,.xlsx"
                        onChange={handleImportChange}
                        className="hidden" 
                        id="import-alumni"
                        disabled={isImporting}
                    />
                     <label 
                        htmlFor="import-alumni"
                        className={`w-fit self-end font-lufga ${isImporting ? 'bg-gray-400 cursor-not-allowed' : 'bg-gray-200 hover:opacity-90'} focus:outline-none rounded-md shadow-md text-base px-4 py-2 sm:px-10 sm:py-2 text-center cursor-pointer flex items-center gap-2`}
                    >
                        {isImporting ? (
                            <>
                                <Oval
                                    visible={true}
                                    height="20"
                                    width="20"
                                    color="#000"
                                    ariaLabel="oval-loading"
                                    wrapperStyle={{}}
                                    wrapperClass=""
                                />
                                Importing...
                            </>
                        ) : (
                            'Import CSV'
                        )}
                    </label>
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center items-center h-[80vh]">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500"></div>
                </div>
            ) : (
                <>
                    {alumni.length > 0 ? (
                        <div className='my-6'>
                            <AlumniTable 
                                alumni={alumni} 
                                onView={(alumni) => setSelectedAlumni(alumni)}
                                onDelete={handleDeleteAlumni}
                            />
                        </div>
                    ) : (
                        <div className="text-center py-4 text-gray-500">
                            No Alumni found
                        </div>
                    )}

                    <Pagination
                        currentPage={pagination.currentPage}
                        lastPage={pagination.lastPage}
                        total={pagination.total}
                        itemRange={getItemRange()}
                        pageNumbers={getPageNumbers()}
                        onPageChange={handlePageChange}
                    />
                </>
            )}

            {/* Alumni View/Edit Modal */}
            {selectedAlumni && (
                <AlumniModal 
                    alumni={selectedAlumni}
                    onClose={() => setSelectedAlumni(null)}
                    onEdit={handleEditAlumni}
                />
            )}

            {/* Add Alumni Modal */}
            {isAddModalOpen && (
                <AddAlumniModal 
                    onClose={() => setIsAddModalOpen(false)}
                    onAdd={handleAddAlumni}
                />
            )}
        </div>
    );
};

export default Alumni;